import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { trackEvent, WEB_BLOG_VIEW } from '../lib/analytics';
import styles from './BlogPost.module.css';
import { SectionWrapper } from '../components/shared/SectionWrapper';
import { Chip } from '../components/shared/Chip';
import { ScriptText } from '../components/shared/ScriptText';
import { Button } from '../components/shared/Button';
import { allPosts, blogPosts, blogContent } from '../data/blog-posts';
import { useSEO } from '../hooks/useSEO';

function renderBody(markdown: string) {
  const lines = markdown.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(<h2 key={i}>{line.slice(3)}</h2>);
    } else if (line.startsWith('> ')) {
      const quoteText = line.slice(2).replace(/^"/, '').replace(/"$/, '');
      elements.push(
        <blockquote key={i}>
          <span className={styles.bqGlow}>"{quoteText}"</span>
        </blockquote>,
      );
    } else if (line.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`}>
          {items.map((item, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          ))}
        </ul>,
      );
      continue;
    } else if (line.trim()) {
      elements.push(
        <p key={i} dangerouslySetInnerHTML={{
          __html: line
            .replace(/\*\*(.*?)\*\*/g, '<strong style="color:var(--accent)">$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>'),
        }} />,
      );
    }
    i++;
  }

  return elements;
}

export function BlogPost() {
  const { slug } = useParams();
  const post = allPosts.find((p) => p.slug === slug) ?? blogPosts[0];
  const related = allPosts.filter((p) => p.slug !== post.slug).slice(0, 3);
  const content = blogContent[post.slug];

  useSEO({
    title: post.title,
    description: post.excerpt,
    keywords: `${post.kicker.toLowerCase()}, couples advice, relationship tips, luvlust`,
    canonical: `https://luvlustapp.com/blog/${post.slug}`,
  });

  useEffect(() => {
    if (post.slug) {
      void trackEvent(WEB_BLOG_VIEW, { slug: post.slug });
    }
  }, [post.slug]);

  const titleWords = post.title.split(' ');
  const firstWord = titleWords[0];
  const rest = titleWords.slice(1).join(' ');

  return (
    <>
      {/* Header */}
      <SectionWrapper className={styles.header}>
        <Link to="/blog" className={styles.back}>&#8592; Back to Journal</Link>
        <Chip>{post.kicker}</Chip>
        <h1 className={`display ${styles.h1}`}>
          <ScriptText rotate={-2}>{firstWord}</ScriptText> {rest}
        </h1>
        <p className={styles.subtitle}>{post.excerpt}</p>
        <div className={styles.authorRow}>
          <div className={styles.avatar} />
          <div>
            <span className={styles.authorName}>{post.author}</span>
            <span className={styles.authorRole}>{post.authorRole}</span>
          </div>
          <span className={styles.divider} />
          <div>
            <span className={styles.date}>{post.date}</span>
            <span className={styles.readTime}>{post.readTime}</span>
          </div>
        </div>
      </SectionWrapper>

      {/* Cover Image */}
      <div className={styles.cover}>
        <div className={styles.coverInner} style={{ background: `linear-gradient(135deg, ${post.hue}33, var(--bg))` }}>
          <span className={styles.coverEmoji}>{post.emoji}</span>
        </div>
      </div>

      {/* Body */}
      <article className={styles.body}>
        {content ? renderBody(content.body) : <p>{post.excerpt}</p>}
      </article>

      {/* Author Card */}
      <SectionWrapper>
        <div className={styles.authorCard}>
          <div className={styles.authorCardAvatar} />
          <div className={styles.authorCardInfo}>
            <h3 className={styles.authorCardName}>{post.author}</h3>
            <p className={styles.authorCardRole}>{post.authorRole}</p>
            <p className={styles.authorCardBio}>
              {post.author === 'Asha Kapoor'
                ? 'Asha believes intimacy is a skill, not a mystery. She left a therapy practice to build tools that help couples practice it daily.'
                : 'Dr. Osei is a relationship psychologist who designs evidence-based interventions for modern couples. She leads research at LuvLust.'}
            </p>
            <Button variant="ghost">Follow</Button>
          </div>
        </div>
      </SectionWrapper>

      {/* Related */}
      <SectionWrapper>
        <div className={styles.relatedHeader}>
          <h2 className={`display ${styles.relatedTitle}`}>Keep reading</h2>
          <Link to="/blog" className={styles.relatedLink}>View all &#8594;</Link>
        </div>
        <div className={styles.relatedGrid}>
          {related.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className={styles.relatedCard}>
              <div className={styles.relatedImage} style={{ background: `linear-gradient(135deg, ${p.hue}22, var(--bg))` }}>
                <span className={styles.relatedKicker}>{p.kicker}</span>
                <span className={styles.relatedEmoji}>{p.emoji}</span>
              </div>
              <div className={styles.relatedContent}>
                <h3 className={styles.relatedCardTitle}>{p.title}</h3>
                <span className={styles.relatedMeta}>{p.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
      </SectionWrapper>
    </>
  );
}
